import { motion, useReducedMotion } from "framer-motion";
import { Mic, ArrowUp, Sparkles } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";
import { content } from "@/content/fitswag";
import Reveal from "@/components/Reveal";

export default function SwagSection() {
  const { lang } = useLanguage();
  const t = content[lang].swag;
  const reduce = useReducedMotion();
  const scrollDownload = () =>
    document.getElementById("download")?.scrollIntoView({ behavior: "smooth" });

  return (
    <section
      id="swag"
      className="relative overflow-hidden border-t border-white/10 bg-black py-24 md:py-32"
    >
      <div className="pointer-events-none absolute right-1/3 top-1/4 h-[420px] w-[420px] rounded-full bg-brand-orange/15 blur-[140px]" />
      <div className="relative mx-auto grid max-w-7xl items-center gap-14 px-5 md:grid-cols-2 md:px-8">
        <Reveal>
          <p className="overline flex items-center gap-2 text-brand-orange">
            <Sparkles size={16} /> {t.overline}
          </p>
          <h2 className="mt-4 font-display text-4xl leading-[1.02] tracking-tighter text-white sm:text-5xl md:text-6xl">
            {t.title}
          </h2>
          <p className="mt-6 max-w-lg text-lg leading-relaxed text-white/70">{t.subtitle}</p>
          <ul className="mt-8 space-y-4">
            {t.points.map((p, i) => (
              <li key={i} className="flex items-start gap-3 text-white/85">
                <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-brand-orange" />
                <span className="leading-relaxed">{p}</span>
              </li>
            ))}
          </ul>
          <button
            data-testid="swag-cta"
            onClick={scrollDownload}
            className="mt-9 rounded-full bg-brand-orange px-7 py-3.5 text-sm font-bold text-white transition-transform duration-200 hover:-translate-y-0.5 active:scale-[0.98]"
          >
            {t.cta}
          </button>
        </Reveal>

        <Reveal delay={0.15}>
          <div
            data-testid="swag-chat-mock"
            className="relative mx-auto w-full max-w-md rounded-[2rem] border border-white/10 bg-[#111]/90 p-5 shadow-2xl backdrop-blur"
          >
            <div className="space-y-3">
              {t.chat.map((m, i) => (
                <motion.div
                  key={i}
                  initial={reduce ? false : { opacity: 0, y: 14 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.4 }}
                  transition={{ delay: i * 0.25, duration: 0.5 }}
                  className={m.role === "user" ? "flex justify-end" : "flex justify-start"}
                >
                  <div
                    className={`max-w-[82%] rounded-2xl px-4 py-3 text-sm leading-relaxed ${
                      m.role === "user"
                        ? "bg-brand-orange font-semibold text-black"
                        : "border border-white/10 bg-white/5 text-white/90"
                    }`}
                  >
                    {m.text}
                  </div>
                </motion.div>
              ))}
            </div>
            <div className="mt-5 flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-4 py-2.5">
              <span className="flex-1 truncate text-sm text-white/40">{t.placeholder}</span>
              <Mic size={18} className="shrink-0 text-white/50" />
              <span className="flex h-8 w-8 items-center justify-center rounded-full bg-brand-orange">
                <ArrowUp size={16} className="text-black" />
              </span>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
